import React, { useState, useEffect, useCallback } from 'react';
import { Input, Modal, Spin, InputNumber, Select, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useBonusDistForm } from './hooks/useBonusDistForm';
import SharedActionBar from '../../components/SharedActionBar/SharedActionBar';
import apiClient from '../../services/apiClient';

const PW9236F1Form: React.FC = () => {
  const f = useBonusDistForm();
  const navigate = useNavigate();
  const [searchEmp, setSearchEmp] = useState('');
  const [searchBonus, setSearchBonus] = useState('');
  const [bonusTypeOptions, setBonusTypeOptions] = useState<{ value: string; label: string }[]>([]);

  const loadBonusTypes = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/lookup/bonusType', { params: { keyword: '' } });
      const items = res.data?.data || res.data || [];
      if (Array.isArray(items)) {
        setBonusTypeOptions(items.map((item: any) => ({
          value: item.code || item.F933_01 || '',
          label: `${item.code || item.F933_01 || ''} - ${item.name || item.F933_11 || ''}`,
        })));
      }
    } catch {
      // Fallback: no options
    }
  }, []);

  useEffect(() => { loadBonusTypes(); }, [loadBonusTypes]);

  const handleDelete = () => {
    if (!f.current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除員工「${f.current.f9460_01}」的獎金發放別「${f.current.f9460_02}」？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: f.doDelete,
    });
  };

  const handleQuickSearch = () => {
    if (searchEmp.trim() || searchBonus.trim()) {
      f.quickSearch(searchEmp.trim(), searchBonus.trim());
    }
  };

  const fmt = (n: number) => n.toLocaleString('zh-TW');

  return (
    <Spin spinning={f.loading}>
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '16px 24px' }}>
        {/* Title */}
        <h2 style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 12 }}>
          獎金發放記錄 (pw9236f1)
          <span style={{ marginLeft: 12, fontSize: 13, fontWeight: 'normal', color: '#888' }}>
            [{f.mode === 'add' ? '新增' : f.mode === 'edit' ? '修改' : '瀏覽'}]
            {f.records.length > 0 ? ` ${f.currentIdx + 1} / ${f.records.length}` : ' 無資料'}
          </span>
        </h2>

        {/* Row 1 (top ~120): 員工代號 | 部門代號 — FoxPro left+right column */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <label style={{ width: 80, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>員工代號</label>
          <Input
            value={f.empCode}
            onChange={e => f.setEmpCode(e.target.value)}
            maxLength={8}
            disabled={f.mode !== 'add'}
            style={{ width: 140 }}
          />
          <div style={{ flex: 1 }} />
          <label style={{ width: 60, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>部門代號</label>
          <Input
            value={f.deptCode}
            onChange={e => f.setDeptCode(e.target.value)}
            maxLength={4}
            disabled={!f.isEditing}
            style={{ width: 60 }}
          />
        </div>

        {/* Row 2 (top ~150): | 部門名稱 (right column only) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ width: 80, flexShrink: 0 }} />
          <div style={{ width: 140 }} />
          <div style={{ flex: 1 }} />
          <label style={{ width: 60, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>部門名稱</label>
          <Input
            value={f.refData?.f106_11 || ''}
            readOnly
            disabled
            style={{ width: 150 }}
          />
        </div>

        {/* Row 3 (top ~207): 獎金發放別 (Select) | 發放別名稱 (RO) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <label style={{ width: 80, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>獎金發放別</label>
          <Select
            showSearch
            value={f.bonusType || undefined}
            onChange={(val: string) => f.setBonusType(val)}
            disabled={f.mode !== 'add'}
            style={{ width: 140 }}
            placeholder="選擇發放別"
            options={bonusTypeOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            allowClear
          />
          <div style={{ flex: 1 }} />
          <Input
            value={f.refData?.f933_11 || ''}
            readOnly
            disabled
            style={{ width: 200 }}
            placeholder="發放別名稱"
          />
        </div>

        {/* Row 4 (top ~244): | 發放日期 (right column only) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ width: 80, flexShrink: 0 }} />
          <div style={{ width: 140 }} />
          <div style={{ flex: 1 }} />
          <label style={{ width: 60, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>發放日期</label>
          <Input
            value={f.refData?.f933_12 || ''}
            readOnly
            disabled
            style={{ width: 120 }}
          />
        </div>

        {/* Row 5 (top ~276): 金額 | 稅率 (RO) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <label style={{ width: 80, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>金額</label>
          <InputNumber
            value={f.amount}
            onChange={v => f.setAmount(v ?? 0)}
            disabled={!f.isEditing}
            style={{ width: 140 }}
            min={0}
            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          />
          <div style={{ flex: 1 }} />
          <label style={{ width: 36, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>稅率</label>
          <Input
            value={f.refData?.f933_22 != null ? `${f.refData.f933_22}%` : ''}
            readOnly
            disabled
            style={{ width: 80 }}
          />
        </div>

        {/* Row 6 (top ~320): 代金 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <label style={{ width: 80, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>代金</label>
          <InputNumber
            value={f.subsidy}
            onChange={v => f.setSubsidy(v ?? 0)}
            disabled={!f.isEditing}
            style={{ width: 140 }}
            min={0}
            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          />
        </div>

        {/* Row 7-8 (top ~363-406): 稅額 (computed) | 實發 (computed) — FoxPro LBL_TAX + LBL_SUB */}
        <div style={{ display: 'flex', gap: 24, marginBottom: 12, marginLeft: 88 }}>
          <div style={{ fontSize: 13 }}>
            <span style={{ color: '#666' }}>稅額：</span>
            <span style={{ fontWeight: 500 }}>{fmt(f.tax)}</span>
          </div>
          <div style={{ fontSize: 13 }}>
            <span style={{ color: '#666' }}>實發：</span>
            <span style={{ fontWeight: 'bold' }}>{fmt(f.netPay)}</span>
          </div>
        </div>

        {/* Quick search (FoxPro QTXT1+QTXT2, right side) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, justifyContent: 'flex-end' }}>
          <span style={{ fontSize: 12, color: '#888' }}>快速查詢：</span>
          <Input
            placeholder="員工代號"
            value={searchEmp}
            onChange={e => setSearchEmp(e.target.value)}
            onPressEnter={handleQuickSearch}
            disabled={f.isEditing}
            style={{ width: 100 }}
            maxLength={8}
            size="small"
          />
          <Input
            placeholder="獎金發放別"
            value={searchBonus}
            onChange={e => setSearchBonus(e.target.value)}
            onPressEnter={handleQuickSearch}
            disabled={f.isEditing}
            style={{ width: 100 }}
            maxLength={10}
            size="small"
          />
          <button className="sab-btn" onClick={handleQuickSearch} disabled={f.isEditing} style={{ fontSize: 12 }}>查詢</button>
        </div>

        {/* SharedActionBar — replaces inline buttons, matches FoxPro Pwtcmd1 */}
        <SharedActionBar
          mode={f.mode}
          isEditing={f.isEditing}
          hasRecord={!!f.current}
          currentIdx={f.currentIdx}
          recordCount={f.records.length}
          onNavFirst={f.navFirst}
          onNavPrev={f.navPrev}
          onNavNext={f.navNext}
          onNavLast={f.navLast}
          onAdd={f.startAdd}
          onModify={f.startEdit}
          onDelete={handleDelete}
          onSave={f.save}
          onCancel={f.cancel}
          onExit={() => navigate(-1)}
        />
      </div>
    </Spin>
  );
};

export default PW9236F1Form;
